// =============================================
// ENHANCED PROJECT REPORT WITH BUDGET TRACKING
// components/reports/ProjectReport.js
// =============================================

import React, { useMemo, useState, useCallback } from 'react'
import { FolderOpen, Users, Clock, DollarSign, Calendar, UserCheck, ChevronDown, ChevronRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Target, Info, X } from 'lucide-react'
import { processTimeEntries, calculateEntryCounts } from '../../utils/timeSplitUtils'

// Move formatRoleName outside component to fix initialization error
const formatRoleNameProject = (role) => {
  if (!role || role === 'unassigned') return 'Unassigned'
  
  const roleLabels = {
    'senior_developer': 'Senior Developer',
    'project_manager': 'Project Manager', 
    'developer': 'Developer',
    'designer': 'Designer',
    'qa_engineer': 'QA Engineer',
    'tech_lead': 'Tech Lead',
    'consultant': 'Consultant',
    'architect': 'Architect',
    'junior_developer': 'Junior Developer',
    'devops_engineer': 'DevOps Engineer',
    'business_analyst': 'Business Analyst',
    'product_manager': 'Product Manager'
  }
  
  return roleLabels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

export const ProjectReport = ({ data }) => {
  const [pivotBy, setPivotBy] = useState('role') // 'role' or 'user'
  const [expandedProjects, setExpandedProjects] = useState(new Set())
  const [showBudgetView, setShowBudgetView] = useState(true) // Toggle budget tracking
  const [showHelpModal, setShowHelpModal] = useState(false) // Help modal state

  // Define calculateBudgetMetrics BEFORE projectData useMemo
  const calculateBudgetMetrics = useCallback((project) => {
    if (!project.budgetHours) {
      return {
        hasBudget: false,
        budgetUsedPercent: 0,
        timeElapsedPercent: 0,
        expectedHours: 0,
        variance: 0,
        variancePercent: 0,
        projectedTotal: project.totalHours,
        projectedOverrun: 0,
        healthStatus: 'no-budget',
        isOnTrack: true,
        remainingHours: 0,
        remainingBudget: 0,
        daysElapsed: 0,
        totalDays: 0,
        avgHoursPerDay: 0
      }
    }

    // Calculate time elapsed
    const now = new Date()
    const startDate = project.projectStartDate ? new Date(project.projectStartDate) : new Date(project.firstActivity)
    const endDate = project.projectEndDate ? new Date(project.projectEndDate) : now
    
    const totalDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)))
    const daysElapsed = Math.max(1, Math.ceil((now - startDate) / (1000 * 60 * 60 * 24)))
    const timeElapsedPercent = Math.min((daysElapsed / totalDays) * 100, 100)
    
    // Budget calculations
    const budgetUsedPercent = (project.totalHours / project.budgetHours) * 100
    const expectedHours = (project.budgetHours * timeElapsedPercent) / 100
    const variance = project.totalHours - expectedHours
    const variancePercent = expectedHours > 0 ? (variance / expectedHours) * 100 : 0
    
    // Projections
    const avgHoursPerDay = project.totalHours / daysElapsed
    const projectedTotal = totalDays > 0 ? avgHoursPerDay * totalDays : project.totalHours
    const projectedOverrun = projectedTotal - project.budgetHours
    
    // Health status determination
    let healthStatus = 'healthy'
    if (budgetUsedPercent > 100 || (budgetUsedPercent > timeElapsedPercent && variancePercent > 20)) {
      healthStatus = 'critical'
    } else if (budgetUsedPercent > 90 || (budgetUsedPercent > timeElapsedPercent && variancePercent > 10)) {
      healthStatus = 'warning'
    }

    return {
      hasBudget: true,
      budgetUsedPercent,
      timeElapsedPercent,
      expectedHours,
      variance,
      variancePercent,
      projectedTotal,
      projectedOverrun,
      healthStatus,
      isOnTrack: variance <= 0 || variancePercent <= 10,
      remainingHours: project.budgetHours - project.totalHours,
      remainingBudget: project.budgetAmount ? project.budgetAmount - project.revenue : 0,
      daysElapsed,
      totalDays,
      avgHoursPerDay
    }
  }, [])

  const projectData = useMemo(() => {
    // Process time entries to handle splits
    const expandedEntries = processTimeEntries(data)
    const projectMap = new Map()
    
    // Keep track of original entries per project
    const projectOriginalCounts = new Map()
    data.forEach(entry => {
      const key = entry.project_id
      projectOriginalCounts.set(key, (projectOriginalCounts.get(key) || 0) + 1)
    })
    
    expandedEntries.forEach(entry => {
      const key = entry.project_id
      if (!projectMap.has(key)) {
        projectMap.set(key, {
          id: key,
          name: entry.project_name,
          clientName: entry.client_name,
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          expandedEntries: 0,
          splitCount: 0,
          users: new Set(),
          lastActivity: entry.entry_date,
          firstActivity: entry.entry_date,
          // Budget fields - these would come from project table
          budgetHours: entry.project_budget_hours || null,
          budgetAmount: entry.project_budget_amount || null,
          projectStartDate: entry.project_start_date || null,
          projectEndDate: entry.project_end_date || null,
          roles: new Map(),
          userDetails: new Map()
        })
      }
      
      const project = projectMap.get(key)
      project.totalHours += entry.duration_hours || 0
      project.expandedEntries += 1
      if (entry.is_split_entry) project.splitCount += 1
      project.users.add(entry.user_id)
      
      if (entry.entry_date > project.lastActivity) {
        project.lastActivity = entry.entry_date
      }
      if (entry.entry_date < project.firstActivity) {
        project.firstActivity = entry.entry_date
      }
      
      // Track by role for pivot
      const role = entry.bill_rate_role || 'unassigned'
      if (!project.roles.has(role)) {
        project.roles.set(role, {
          role: role,
          roleName: formatRoleNameProject(role),
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0,
          users: new Set()
        })
      }
      const roleData = project.roles.get(role)
      roleData.totalHours += entry.duration_hours || 0
      roleData.entries += 1
      roleData.users.add(entry.user_id)
      if (entry.is_billable) {
        roleData.billableHours += entry.duration_hours || 0
        roleData.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
      
      // Track by user for pivot
      const userId = entry.user_id
      if (!project.userDetails.has(userId)) {
        project.userDetails.set(userId, {
          userId: userId,
          userName: entry.user_name,
          role: entry.bill_rate_role || 'unassigned',
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0
        })
      }
      const userData = project.userDetails.get(userId)
      userData.totalHours += entry.duration_hours || 0
      userData.entries += 1
      if (entry.is_billable) {
        userData.billableHours += entry.duration_hours || 0
        userData.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
      
      if (entry.is_billable) {
        project.billableHours += entry.duration_hours || 0
        project.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Array.from(projectMap.values()).map(project => {
      // Calculate budget metrics
      const budgetMetrics = calculateBudgetMetrics(project)
      
      return {
        ...project,
        userCount: project.users.size,
        utilizationRate: project.totalHours > 0 ? (project.billableHours / project.totalHours) * 100 : 0,
        avgRate: project.billableHours > 0 ? project.revenue / project.billableHours : 0,
        originalEntries: projectOriginalCounts.get(project.id) || 0,
        ...budgetMetrics,
        rolesList: Array.from(project.roles.values()).map(role => ({
          ...role,
          userCount: role.users.size,
          utilizationRate: role.totalHours > 0 ? (role.billableHours / role.totalHours) * 100 : 0,
          avgRate: role.billableHours > 0 ? role.revenue / role.billableHours : 0
        })).sort((a, b) => b.revenue - a.revenue),
        usersList: Array.from(project.userDetails.values()).map(user => ({
          ...user,
          roleName: formatRoleNameProject(user.role),
          utilizationRate: user.totalHours > 0 ? (user.billableHours / user.totalHours) * 100 : 0,
          avgRate: user.billableHours > 0 ? user.revenue / user.billableHours : 0
        })).sort((a, b) => b.revenue - a.revenue)
      }
    }).sort((a, b) => b.totalHours - a.totalHours)
  }, [data, calculateBudgetMetrics])

  const getHealthColor = (status) => {
    switch(status) {
      case 'healthy': return 'text-green-600 bg-green-50 border-green-200'
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'critical': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-slate-400 bg-slate-50 border-slate-200'
    }
  }

  const getHealthIcon = (status) => {
    switch(status) {
      case 'healthy': return <CheckCircle className="w-5 h-5" />
      case 'warning': return <AlertTriangle className="w-5 h-5" />
      case 'critical': return <TrendingDown className="w-5 h-5" />
      default: return <Target className="w-5 h-5" />
    }
  }

  const toggleProjectExpanded = (projectId) => {
    const newExpanded = new Set(expandedProjects)
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId)
    } else {
      newExpanded.add(projectId)
    }
    setExpandedProjects(newExpanded)
  }

  // Calculate budget summary
  const budgetSummary = useMemo(() => {
    const projectsWithBudget = projectData.filter(p => p.hasBudget)
    if (projectsWithBudget.length === 0) return null

    return {
      totalProjects: projectsWithBudget.length,
      totalBudgetHours: projectsWithBudget.reduce((sum, p) => sum + (p.budgetHours || 0), 0),
      totalActualHours: projectsWithBudget.reduce((sum, p) => sum + p.totalHours, 0),
      totalBudgetAmount: projectsWithBudget.reduce((sum, p) => sum + (p.budgetAmount || 0), 0),
      totalActualAmount: projectsWithBudget.reduce((sum, p) => sum + p.revenue, 0),
      projectsOnTrack: projectsWithBudget.filter(p => p.isOnTrack).length,
      projectsAtRisk: projectsWithBudget.filter(p => p.healthStatus === 'warning').length,
      projectsCritical: projectsWithBudget.filter(p => p.healthStatus === 'critical').length
    }
  }, [projectData])

  if (!projectData.length) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No project data found for the selected filters</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Budget Tracking Guide</h3>
                  <p className="text-sm text-slate-600">Understanding project health and metrics</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Health Status Section */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-4">Project Health Status</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-green-900 mb-1">Healthy (Green)</div>
                      <div className="text-sm text-green-800 space-y-1">
                        <p>• Budget used is on track with time elapsed</p>
                        <p>• Variance is within 10% of expected pace</p>
                        <p>• Project is under budget or ahead of schedule</p>
                      </div>
                      <div className="mt-2 text-xs text-green-700 bg-green-100 px-2 py-1 rounded inline-block">
                        Example: 50% of time passed, 45-55% of budget used
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-yellow-900 mb-1">Warning (Yellow)</div>
                      <div className="text-sm text-yellow-800 space-y-1">
                        <p>• Budget used is 90% or higher, OR</p>
                        <p>• Budget used exceeds time elapsed by 10-20%</p>
                        <p>• Project needs monitoring to avoid overrun</p>
                      </div>
                      <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded inline-block">
                        Example: 50% of time passed, 65% of budget used (15% over pace)
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <TrendingDown className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-red-900 mb-1">Critical (Red)</div>
                      <div className="text-sm text-red-800 space-y-1">
                        <p>• Budget is exceeded (over 100%), OR</p>
                        <p>• Budget used exceeds time elapsed by more than 20%</p>
                        <p>• Immediate action required to prevent significant overrun</p>
                      </div>
                      <div className="mt-2 text-xs text-red-700 bg-red-100 px-2 py-1 rounded inline-block">
                        Example: 50% of time passed, 75% of budget used (25% over pace)
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics Section */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-4">Key Metrics Explained</h4>
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-slate-900">Budget Used %</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">
                      Percentage of total budget hours that have been consumed.
                    </p>
                    <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                      <strong>Formula:</strong> (Actual Hours / Budget Hours) × 100
                      <br />
                      <strong>Example:</strong> 142.5h used / 160h budget = 89.1%
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-slate-900">Expected Hours</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">
                      How many hours you should have used based on time elapsed.
                    </p>
                    <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                      <strong>Formula:</strong> Budget Hours × (Days Elapsed / Total Days)
                      <br />
                      <strong>Example:</strong> 160h × (37 days / 90 days) = 65.8h expected
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-slate-900">Variance</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">
                      Difference between actual hours used and expected hours at this point.
                    </p>
                    <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                      <strong>Formula:</strong> Actual Hours - Expected Hours
                      <br />
                      <strong>Example:</strong> 142.5h actual - 65.8h expected = +76.7h (over pace)
                      <br />
                      <strong>Positive:</strong> Using more hours than expected (behind schedule)
                      <br />
                      <strong>Negative:</strong> Using fewer hours than expected (ahead of schedule)
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold text-slate-900">Projected Total</span>
                    </div>
                    <p className="text-sm text-slate-700 mb-2">
                      Estimated total hours at project completion based on current burn rate.
                    </p>
                    <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
                      <strong>Formula:</strong> (Actual Hours / Days Elapsed) × Total Days
                      <br />
                      <strong>Example:</strong> (142.5h / 37 days) × 90 days = 346.6h projected
                      <br />
                      <strong>Overrun:</strong> 346.6h - 160h budget = +186.6h over budget
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Metrics Section */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-4">Summary Card Metrics</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                      <Target className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">Projects with Budget:</span>
                      <span className="text-slate-700 ml-1">Total count of projects that have budget hours defined</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">Budget Hours:</span>
                      <span className="text-slate-700 ml-1">Sum of all budget hours vs. actual hours used across projects</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">Budget Amount:</span>
                      <span className="text-slate-700 ml-1">Total budget  vs. actual cost generated from billable hours</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">Overall Health:</span>
                      <span className="text-slate-700 ml-1">Percentage of projects that are "on track" (healthy status or variance ≤ 10%)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  Pro Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-1.5 ml-7">
                  <li>• Review projects in "Warning" status weekly to prevent them from becoming critical</li>
                  <li>• Set realistic project budgets with 10-15% buffer for unexpected work</li>
                  <li>• Use projected totals to have early conversations with clients about scope changes</li>
                  <li>• Track variance trends - consistent positive variance may indicate unrealistic budgets</li>
                  <li>• Projects without budgets won't appear in health calculations</li>
                </ul>
              </div>
            </div>

            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4">
              <button
                onClick={() => setShowHelpModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Help Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Performance</h2>
          <p className="text-slate-600 mt-1">Track project budgets, health, and team performance</p>
        </div>
       
      </div>

      {/* Budget Summary Cards - Only show if projects have budgets */}
      {showBudgetView && budgetSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm font-medium">Projects with Budget</span>
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900">{budgetSummary.totalProjects}</div>
            <div className="mt-2 flex items-center gap-4 text-xs">
              <span className="text-green-600">✓ {budgetSummary.projectsOnTrack} on track</span>
              <span className="text-red-600">⚠ {budgetSummary.projectsAtRisk + budgetSummary.projectsCritical} at risk</span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm font-medium">Budget Hours</span>
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900">{budgetSummary.totalBudgetHours.toFixed(1)}h</div>
            <div className="mt-2 text-sm text-slate-600">
              {budgetSummary.totalActualHours.toFixed(1)}h used ({((budgetSummary.totalActualHours / budgetSummary.totalBudgetHours) * 100).toFixed(1)}%)
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm font-medium">Budget Amount</span>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900">
              ${budgetSummary.totalBudgetAmount.toLocaleString()}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              ${budgetSummary.totalActualAmount.toLocaleString()} spent ({((budgetSummary.totalActualAmount / budgetSummary.totalBudgetAmount) * 100).toFixed(1)}%)
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm font-medium">Overall Health</span>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900">
              {((budgetSummary.projectsOnTrack / budgetSummary.totalProjects) * 100).toFixed(0)}%
            </div>
            <div className="mt-2 text-sm text-slate-600">
              Projects on track
            </div>
          </div>
        </div>
      )}

      {/* Project Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projectData.slice(0, 6).map((project) => (
          <div key={project.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">{project.name}</h3>
                <p className="text-sm text-slate-600 truncate">{project.clientName}</p>
                {project.splitCount > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    {project.splitCount} split entries
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {project.hasBudget && (
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getHealthColor(project.healthStatus)}`}>
                    {getHealthIcon(project.healthStatus)}
                  </div>
                )}
                <FolderOpen className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Hours</span>
                </div>
                <span className="font-medium text-slate-900">
                  {project.totalHours.toFixed(1)}h
                  {project.hasBudget && <span className="text-slate-500 text-sm ml-1">/ {project.budgetHours}h</span>}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 mr-1" />
                  <span>Cost</span>
                </div>
                <span className="font-medium text-green-600">${project.revenue.toFixed(0)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <Users className="w-4 h-4 mr-1" />
                  <span>Team</span>
                </div>
                <span className="font-medium text-slate-900">{project.userCount}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>Last Activity</span>
                </div>
                <span className="text-sm text-slate-900">
                  {new Date(project.lastActivity).toLocaleDateString()}
                </span>
              </div>
              
              {/* Budget Progress Bar */}
              {showBudgetView && project.hasBudget ? (
                <div className="pt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-600">Budget Used</span>
                    <span className="text-xs font-medium text-slate-900">{project.budgetUsedPercent.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        project.budgetUsedPercent > 100 ? 'bg-red-500' :
                        project.budgetUsedPercent > 90 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(project.budgetUsedPercent, 100)}%` }}
                    />
                  </div>
                  {project.projectedOverrun !== 0 && (
                    <div className={`text-xs mt-1 ${project.projectedOverrun > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      Projected: {project.projectedOverrun > 0 ? '+' : ''}{project.projectedOverrun.toFixed(1)}h {project.projectedOverrun > 0 ? 'over' : 'under'}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-600">Utilization</span>
                    <span className="text-xs font-medium text-slate-900">{project.utilizationRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        project.utilizationRate >= 80 ? 'bg-green-500' :
                        project.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(project.utilizationRate, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pivot Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-semibold text-slate-900">All Projects Performance</h4>
              
            </div>
            <p className="text-sm text-slate-600 mt-1">Complete project analysis with budget tracking and pivot analysis</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {budgetSummary && (
              <button
                onClick={() => setShowBudgetView(!showBudgetView)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showBudgetView
                    ? 'bg-green-100 text-green-800'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Target className="w-4 h-4 inline mr-1" />
                Budget View
              </button>
            )}
            
            <span className="text-sm text-slate-700"> <button
                onClick={() => setShowHelpModal(true)}
                className="text-blue-600 hover:text-blue-700 transition-colors"
                title="Learn about budget tracking"
              >
                <Info className="w-5 h-5" />
              </button></span><span className="text-sm text-slate-700">Pivot by:</span>
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setPivotBy('role')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  pivotBy === 'role'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <UserCheck className="w-4 h-4 inline mr-1" />
                Role
              </button>
              <button
                onClick={() => setPivotBy('user')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  pivotBy === 'user'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hierarchical Project Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Project / {pivotBy === 'role' ? 'Role' : 'Team Member'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Client
                </th>
                {showBudgetView && budgetSummary && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Budget Status
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {pivotBy === 'role' ? 'Team Members' : 'Role'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {showBudgetView && budgetSummary ? 'Progress' : 'Utilization'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Last Activity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {projectData.map((project, projectIndex) => {
                const isExpanded = expandedProjects.has(project.id)
                const pivotData = pivotBy === 'role' ? project.rolesList : project.usersList
                
                return (
                  <React.Fragment key={project.id}>
                    {/* Project Summary Row */}
                    <tr 
                      className={`${projectIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => toggleProjectExpanded(project.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400 mr-2" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400 mr-2" />
                          )}
                          <div>
                            <div className="font-medium text-slate-900">{project.name}</div>
                            <div className="text-sm text-slate-500">
                              {project.originalEntries} entries{project.splitCount > 0 ? ` (+${project.splitCount} splits)` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {project.clientName}
                      </td>
                      {showBudgetView && budgetSummary && (
                        <td className="px-6 py-4">
                          {project.hasBudget ? (
                            <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs w-fit ${getHealthColor(project.healthStatus)}`}>
                              {getHealthIcon(project.healthStatus)}
                              <span className="font-medium capitalize">{project.healthStatus}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No budget</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {pivotBy === 'role' ? `${project.userCount} members` : formatRoleNameProject(project.usersList[0]?.role)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">
                          {project.totalHours.toFixed(1)}h
                        </div>
                        {showBudgetView && project.hasBudget && (
                          <div className="text-xs text-slate-500">
                            of {project.budgetHours}h
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-green-600">
                        ${project.revenue.toFixed(0)}
                      </td>
                      <td className="px-6 py-4">
                        {showBudgetView && project.hasBudget ? (
                          <div className="flex items-center">
                            <div className="w-12 bg-slate-200 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  project.budgetUsedPercent > 100 ? 'bg-red-500' :
                                  project.budgetUsedPercent > 90 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(project.budgetUsedPercent, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm text-slate-900">
                              {project.budgetUsedPercent.toFixed(1)}%
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="w-12 bg-slate-200 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  project.utilizationRate >= 80 ? 'bg-green-500' :
                                  project.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(project.utilizationRate, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm text-slate-900">
                              {project.utilizationRate.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(project.lastActivity).toLocaleDateString()}
                      </td>
                    </tr>

                    {/* Pivot Sub-rows */}
                    {isExpanded && pivotData.map((item, itemIndex) => (
                      <tr key={`${project.id}-${pivotBy}-${itemIndex}`} className="bg-slate-25 border-l-4 border-l-blue-200">
                        <td className="px-6 py-3 pl-12">
                          <div className="flex items-center text-sm">
                            {pivotBy === 'role' ? (
                              <>
                                <UserCheck className="w-4 h-4 text-slate-400 mr-2" />
                                <span className="font-medium text-slate-700">{item.roleName}</span>
                              </>
                            ) : (
                              <>
                                <Users className="w-4 h-4 text-slate-400 mr-2" />
                                <span className="font-medium text-slate-700">{item.userName}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-600">-</td>
                        {showBudgetView && budgetSummary && (
                          <td className="px-6 py-3 text-sm text-slate-600">-</td>
                        )}
                        <td className="px-6 py-3 text-sm text-slate-600">
                          {pivotBy === 'role' ? item.userCount : item.roleName}
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-700">
                          {item.totalHours.toFixed(1)}h
                        </td>
                        <td className="px-6 py-3 text-sm font-medium text-green-600">
                          ${item.revenue.toFixed(0)}
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center">
                            <div className="w-10 bg-slate-200 rounded-full h-1.5 mr-2">
                              <div 
                                className={`h-1.5 rounded-full ${
                                  item.utilizationRate >= 80 ? 'bg-green-500' :
                                  item.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(item.utilizationRate, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-700">
                              {item.utilizationRate.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-600">-</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ProjectReport